/**
 * Cita Events - Checkout Core Pricing Module
 * ==========================================
 * Unified Price Calculation for all Checkout Implementations
 *
 * This module handles:
 * - Base price calculation
 * - Tier pricing (volume discounts)
 * - Group discounts (percentage or fixed amount)
 * - Promo code discounts
 * - Plus-one pricing (free add-ons)
 *
 * @version 1.1.0
 * @module checkout-core/pricing
 */

/**
 * Round to 2 decimal places, matching Python round(..., 2) in stripe_checkout.py.
 */
function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Calculate the total price for a checkout.
 *
 * @param {Object} state - Current checkout state
 * @param {number} state.quantity - Number of paid tickets
 * @param {number} [state.promoDiscount=0] - Promo code discount amount
 * @param {string} [state.promoCode=null] - Applied promo code
 * @param {Array} [state.plusOnes=[]] - Array of plus-one persons (free)
 * @param {Object} ticketData - Ticket configuration
 * @param {number} ticketData.price - Base ticket price
 * @param {string} ticketData.currency - Currency code (e.g., 'EUR')
 * @param {Array} [ticketData.tiers=[]] - Tier pricing array
 * @param {boolean} [ticketData.allowGroup=false] - Whether group booking is allowed
 * @param {string} [ticketData.groupDiscountType] - 'Prozent' or 'Betrag'
 * @param {number} [ticketData.groupDiscountValue=0] - Discount value
 * @param {number} [ticketData.groupMin=2] - Minimum quantity for group discount
 * @returns {Object} Price calculation result
 */
export function calculatePrice(state, ticketData) {
  if (!ticketData || (ticketData.price == null && (!ticketData.tiers || ticketData.tiers.length === 0))) {
    const qty = Math.max(1, parseInt(state?.quantity) || 1);
    const free = Array.isArray(state?.plusOnes) ? state.plusOnes.length : 0;
    return {
      subtotal: 0,
      discount: 0,
      groupDiscount: 0,
      membershipDiscount: 0,
      effectivePromoDiscount: 0,
      effectiveGroupDiscount: 0,
      effectiveMembershipDiscount: 0,
      totalDiscount: 0,
      discountCapApplied: 0,
      total: 0,
      paidQuantity: qty,
      freeQuantity: free,
      totalQuantity: qty + free,
      unitPrice: 0,
      selectedTier: null,
      selectedTierLabel: null,
      currency: ticketData?.currency || 'EUR',
    };
  }

  const {
    price,
    tiers = [],
    allowGroup = false,
    groupDiscountType,
    groupDiscountValue = 0,
    groupMin = 2,
    currency = 'EUR',
    membershipDiscountType = '',
    membershipDiscountValue = 0,
  } = ticketData;

  const {
    quantity = 1,
    promoDiscount = 0,
    plusOnes = [],
    membershipNumber = '',
  } = state;

  let basePrice = price;
  let selectedTier = null;
  let selectedTierLabel = null;

  // =====================
  // Tier Pricing
  // =====================
  // BUG-004 FIX: Find the best applicable tier based on quantity,
  // respecting both min_quantity AND max_quantity bounds
  if (tiers && tiers.length > 0) {
    const applicableTier = tiers
      .filter((t) => {
        const min = t.min_quantity || 1;
        const max = t.max_quantity || 0; // 0 = unlimited
        return quantity >= min && (max === 0 || quantity <= max);
      })
      .sort((a, b) => (b.min_quantity || 1) - (a.min_quantity || 1))[0];

    if (applicableTier) {
      basePrice = applicableTier.price;
      selectedTier = applicableTier.name;
      selectedTierLabel = applicableTier.label || null;
    }
  }

  // =====================
  // Quantity Calculation
  // =====================
  // Paid quantity = state.quantity (main person + group members)
  // Plus-ones are FREE and don't count towards pricing
  const paidQuantity = Math.max(1, parseInt(quantity) || 1);
  const freeQuantity = plusOnes.length;
  // BUG-4 FIX: Round intermediate results to match BE (stripe_checkout.py:6496)
  const subtotal = round2(basePrice * paidQuantity);

  // =====================
  // Group Discount
  // =====================
  let groupDiscount = 0;

  if (allowGroup && groupDiscountType && paidQuantity >= groupMin) {
    if (groupDiscountType === 'Prozent' && groupDiscountValue > 0) {
      // BUG-4 FIX: Round to match BE (stripe_checkout.py:6328)
      groupDiscount = round2((subtotal * groupDiscountValue) / 100);
    } else if (groupDiscountType === 'Betrag' && groupDiscountValue > 0) {
      // BUG-4 FIX: Round to match BE (stripe_checkout.py:6330)
      groupDiscount = round2(groupDiscountValue * paidQuantity);
    }
  }

  // =====================
  // Membership Discount
  // =====================
  // BUG-1 FIX: Membership discount was missing entirely in the frontend.
  // Backend applies it after group discount, before promo (stripe_checkout.py:6502-6508).
  let membershipDiscount = 0;
  if (membershipNumber && membershipDiscountType && membershipDiscountValue > 0) {
    const baseForMembership = Math.max(0, subtotal - Math.min(groupDiscount, subtotal));
    if (membershipDiscountType === 'Prozent') {
      // BE: round((base_for_membership * discount_value) / 100.0, 2)
      membershipDiscount = round2((baseForMembership * membershipDiscountValue) / 100);
    } else if (membershipDiscountType === 'Betrag') {
      // BE: round(discount_value * max(1, quantity), 2)
      membershipDiscount = round2(membershipDiscountValue * Math.max(1, paidQuantity));
    }
    // Cap at base after group discount (BE: stripe_checkout.py:6508)
    membershipDiscount = Math.min(membershipDiscount, baseForMembership);
  }

  // =====================
  // Promo Discount
  // =====================
  // PromoDiscount is already calculated by validatePromoCode API
  const discount = promoDiscount > 0 ? promoDiscount : 0;

  // =====================
  // Total Calculation
  // =====================
  // S02-006 FIX: Group discount is structural — always applied in full.
  // BUG-2 FIX: Cap applies to membership + promo combined (not promo only).
  // (Backend: stripe_checkout.py ~6544-6570)
  const maxDiscountPct = ticketData.maxCombinedDiscountPercent || 100;

  // Group discount: structural, never capped (but cannot exceed subtotal)
  const effectiveGroupDiscount = Math.min(groupDiscount, subtotal);
  const baseAfterGroup = Math.max(0, subtotal - effectiveGroupDiscount);

  // Stackable discounts: membership + promo, subject to combined cap on post-group base
  let effectiveMembershipDiscount = Math.min(membershipDiscount, baseAfterGroup);
  let effectivePromoDiscount = Math.min(discount, Math.max(0, baseAfterGroup - effectiveMembershipDiscount));
  let discountCapApplied = 0;

  if (maxDiscountPct > 0 && maxDiscountPct < 100 && baseAfterGroup > 0) {
    const stackableDiscount = effectiveMembershipDiscount + effectivePromoDiscount;
    const stackablePct = (stackableDiscount / baseAfterGroup) * 100;
    if (stackablePct > maxDiscountPct) {
      const allowedStackable = round2((maxDiscountPct / 100) * baseAfterGroup);
      const origPromo = effectivePromoDiscount;
      const origMembership = effectiveMembershipDiscount;
      // BE order: reduce promo first, then membership (stripe_checkout.py:6556-6558)
      effectivePromoDiscount = Math.min(effectivePromoDiscount, allowedStackable);
      const remainingForMembership = Math.max(0, allowedStackable - effectivePromoDiscount);
      effectiveMembershipDiscount = Math.min(effectiveMembershipDiscount, remainingForMembership);
      discountCapApplied = (origPromo - effectivePromoDiscount) + (origMembership - effectiveMembershipDiscount);
    }
  }

  const totalDiscount = effectiveGroupDiscount + effectiveMembershipDiscount + effectivePromoDiscount;

  // BUG-5 FIX: Floor at 0 to prevent negative display from float imprecision
  const total = Math.max(0, subtotal - totalDiscount);

  return {
    subtotal,
    discount,
    groupDiscount,
    membershipDiscount,
    effectivePromoDiscount,
    effectiveGroupDiscount,
    effectiveMembershipDiscount,
    totalDiscount,
    discountCapApplied,
    total,
    paidQuantity,
    freeQuantity,
    totalQuantity: paidQuantity + freeQuantity,
    unitPrice: basePrice,
    selectedTier,
    selectedTierLabel,
    currency,
  };
}

/**
 * Format a price value as currency string.
 *
 * @param {number} value - Price value
 * @param {string} [currency='EUR'] - Currency code
 * @param {string} [locale='de-DE'] - Locale for formatting
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, currency = 'EUR', locale = 'de-DE') {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '0,00 ' + getCurrencySymbol(currency);
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    // Fallback formatting
    return value.toFixed(2).replace('.', ',') + ' ' + getCurrencySymbol(currency);
  }
}

/**
 * Get currency symbol for a currency code.
 *
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currency) {
  const symbols = {
    EUR: '\u20AC',
    USD: '$',
    GBP: '\u00A3',
    CHF: 'CHF',
  };
  return symbols[currency] || currency;
}

/**
 * Build a human-readable discount label.
 *
 * @param {Object} priceResult - Result from calculatePrice
 * @param {string} [promoCode=null] - Applied promo code name
 * @returns {string} Discount label
 */
export function buildDiscountLabel(priceResult, promoCode = null) {
  const groupDiscount = typeof priceResult?.effectiveGroupDiscount === 'number'
    ? priceResult.effectiveGroupDiscount
    : priceResult.groupDiscount;
  const membershipDiscount = typeof priceResult?.effectiveMembershipDiscount === 'number'
    ? priceResult.effectiveMembershipDiscount
    : 0;
  const discount = typeof priceResult?.effectivePromoDiscount === 'number'
    ? priceResult.effectivePromoDiscount
    : priceResult.discount;

  const parts = [];
  if (groupDiscount > 0) parts.push('Gruppenrabatt');
  if (membershipDiscount > 0) parts.push('Mitgliederrabatt');
  if (discount > 0) parts.push(promoCode ? `Rabatt (${promoCode})` : 'Rabatt');

  return parts.join(' + ');
}

/**
 * Build ticket quantity label including plus-ones.
 *
 * @param {Object} priceResult - Result from calculatePrice
 * @param {string} ticketName - Name of the ticket type
 * @returns {string} Formatted label
 */
export function buildTicketLabel(priceResult, ticketName = 'Ticket') {
  const { paidQuantity, freeQuantity, selectedTierLabel } = priceResult;

  // Defensive: display quantity must be at least 1 when a ticket is selected
  const displayQty = Math.max(1, paidQuantity || 1);
  let label = `${displayQty}x ${ticketName}`;
  if (selectedTierLabel) {
    label += ` (${selectedTierLabel})`;
  }

  if (freeQuantity > 0) {
    const suffix = freeQuantity > 1 ? 'en' : '';
    label += ` + ${freeQuantity} Begleitperson${suffix} (gratis)`;
  }

  return label;
}

/**
 * Calculate promo discount amount based on type.
 *
 * NOTE: Currently unused — the active promo flow uses the pre-calculated
 * discount_amount from the backend API directly (see applyPromo() in
 * checkout-enhancements.js). Kept as a public utility for potential
 * future use.
 *
 * @param {Object} promo - Promo code data from API
 * @param {number} subtotal - Subtotal before discounts
 * @param {number} quantity - Number of tickets
 * @returns {number} Calculated discount amount
 */
export function calculatePromoDiscount(promo, subtotal, quantity) {
  if (!promo || !promo.valid) {
    return 0;
  }

  const { discount_type, discount_value } = promo;

  if (discount_type === 'Prozent' && discount_value > 0) {
    return (subtotal * discount_value) / 100;
  }

  if (discount_type === 'Betrag' && discount_value > 0) {
    // BUG-7 FIX: Match BE logic (event_promo_code.py:245): min(value, base_price)
    // Previously used discount_value * quantity which diverges from BE.
    return Math.min(discount_value, subtotal);
  }

  if (discount_type === 'Kostenlos') {
    // Kostenlos = 100% Rabatt (Backend: discount_amount = original_price, final_price = 0)
    return subtotal;
  }

  return 0;
}

/**
 * Check if a ticket is free (zero price).
 *
 * @param {Object} ticketData - Ticket configuration
 * @returns {boolean} True if ticket is free
 */
export function isTicketFree(ticketData) {
  return !ticketData?.price || ticketData.price <= 0;
}

/**
 * Get the effective price for display, considering tiers.
 *
 * @param {Object} ticketData - Ticket configuration
 * @param {number} [quantity=1] - Current quantity
 * @returns {number} Effective unit price
 */
export function getEffectivePrice(ticketData, quantity = 1) {
  if (!ticketData || !ticketData.price) {
    return 0;
  }

  const { price, tiers = [] } = ticketData;

  // BUG-004 FIX: Respect max_quantity bounds (consistent with calculatePrice)
  if (tiers && tiers.length > 0) {
    const applicableTier = tiers
      .filter((t) => {
        const min = t.min_quantity || 1;
        const max = t.max_quantity || 0;
        return quantity >= min && (max === 0 || quantity <= max);
      })
      .sort((a, b) => (b.min_quantity || 1) - (a.min_quantity || 1))[0];

    if (applicableTier) {
      return applicableTier.price;
    }
  }

  return price;
}

export default {
  calculatePrice,
  formatCurrency,
  getCurrencySymbol,
  buildDiscountLabel,
  buildTicketLabel,
  calculatePromoDiscount,
  isTicketFree,
  getEffectivePrice,
};
